import { z } from 'zod';
import { MoneySchema } from './api.js';

// Call controls + abuse (setter spec §06.10, §06.10.1–06.10.4, §09.12). Voice is pre-Phase 0: the controls run in the
// own voice service (apps/voice-agent) with test doubles only and are shown «sin verificar» until a real call proves
// them. Values the spec leaves to the owner are per-project settings defaulting to the spec's numbers.

const Iso = z.string();
const Id = z.string().min(1).max(64);

// ───────── Per-project call controls (§06.10.1, §06.10.2, §09.12) ─────────

/** Spec defaults: 30 s silence → «¿Sigues ahí?», +20 s → hang up; ~8 min steer; 12 min usual close; 2 clarifications. */
export const CALL_CONTROL_DEFAULTS = {
  silencePromptSeconds: 30,
  silenceHangupSeconds: 20,
  steerAtSeconds: 480,
  softLimitSeconds: 720,
  maxClarificationAttempts: 2,
} as const;

export const CallControlValuesSchema = z.object({
  silencePromptSeconds: z.number().int().min(10).max(120),
  silenceHangupSeconds: z.number().int().min(10).max(120),
  steerAtSeconds: z.number().int().min(60).max(3600),
  softLimitSeconds: z.number().int().min(120).max(3600),
  /** Absolute cap per call; null = the platform cap (VOICE_MAX_CALL_SECONDS). Never above the platform cap. */
  maxDurationSeconds: z.number().int().min(60).max(3600).nullable(),
  maxClarificationAttempts: z.number().int().min(1).max(3),
});
export type CallControlValues = z.infer<typeof CallControlValuesSchema>;

export const PutCallControlConfigSchema = CallControlValuesSchema.refine((v) => v.steerAtSeconds < v.softLimitSeconds, {
  message: 'La reconducción debe ocurrir antes del cierre habitual',
  path: ['steerAtSeconds'],
}).refine((v) => v.maxDurationSeconds === null || v.softLimitSeconds <= v.maxDurationSeconds, {
  message: 'El cierre habitual no puede ser posterior al tope absoluto',
  path: ['softLimitSeconds'],
});
export type PutCallControlConfig = z.infer<typeof PutCallControlConfigSchema>;

export const CallControlConfigSchema = CallControlValuesSchema.extend({
  projectId: Id,
  /** The platform-wide absolute cap (VOICE_MAX_CALL_SECONDS): the project cap is clamped to it. */
  platformMaxDurationSeconds: z.number().int(),
  /** What a call of this project runs with (project cap clamped to the platform cap). */
  effectiveMaxDurationSeconds: z.number().int(),
  /** true = nothing saved yet: the spec defaults apply. */
  isDefault: z.boolean(),
  updatedAt: Iso.nullable(),
  /** Pre-Phase 0: proven with test doubles only. */
  verified: z.literal(false),
});
export type CallControlConfig = z.infer<typeof CallControlConfigSchema>;

// ───────── What happened on a call ─────────

/** Why the voice service ended a call (stored on the call; never a commercial outcome, never troll/Perdido). */
export const CALL_END_REASONS = ['inactivity', 'duration_soft_limit', 'duration_cap', 'audio_problem', 'abuse_flagged'] as const;
export const CallEndReasonSchema = z.enum(CALL_END_REASONS);
export type CallEndReason = z.infer<typeof CallEndReasonSchema>;
export const CALL_END_REASON_LABELS: Record<CallEndReason, string> = {
  inactivity: 'Inactividad / Sin respuesta',
  duration_soft_limit: 'Cierre por duración (sin avance comercial)',
  duration_cap: 'Tope absoluto de duración',
  audio_problem: 'Problema de audio/comprensión',
  abuse_flagged: 'Posible abuso (en revisión humana)',
};

export const CALL_CONTROLS = ['silence_check', 'steer', 'extended', 'soft_close', 'cap_reached', 'clarification', 'audio_close', 'abuse_close'] as const;
export const CallControlKindSchema = z.enum(CALL_CONTROLS);
export type CallControlKind = z.infer<typeof CallControlKindSchema>;
export const CALL_CONTROL_LABELS: Record<CallControlKind, string> = {
  silence_check: '«¿Sigues ahí?» por silencio',
  steer: 'Reconducción a un siguiente paso',
  extended: 'Extensión justificada (avance comercial)',
  soft_close: 'Cierre habitual por duración',
  cap_reached: 'Tope absoluto alcanzado',
  clarification: 'Intento de aclaración',
  audio_close: 'Cierre por problema de audio/comprensión',
  abuse_close: 'Cierre por posible abuso',
};

export const CallControlEntrySchema = z.object({ at: Iso, control: CallControlKindSchema, elapsedSeconds: z.number().min(0), detail: z.string().nullable() });
export type CallControlEntry = z.infer<typeof CallControlEntrySchema>;

/** Call detail: controls applied by the voice service + duration and estimated cost (§06.10 «registrar»). */
export const CallControlsSummarySchema = z.object({
  endReason: CallEndReasonSchema.nullable(),
  entries: z.array(CallControlEntrySchema),
  durationSeconds: z.number().int().nullable(),
  /** Minutes × the project's per-minute rate (§16 reference unless configured). null = unknown, never 0. */
  estimatedCost: MoneySchema.nullable(),
  /** Pre-Phase 0: controls ran against test doubles only. */
  verified: z.literal(false),
});
export type CallControlsSummary = z.infer<typeof CallControlsSummarySchema>;

// ───────── Abuse: AI flags, a human decides (§06.10.3–4; owner default 2026-10-15) ─────────

/** What the AI may report. Silence, a long call, many questions or audio problems are NOT abuse (§06.10). */
export const ABUSE_CATEGORIES = ['insults_harassment', 'threats', 'sexual_content', 'deliberate_trolling', 'spam_or_bot'] as const;
export const AbuseCategorySchema = z.enum(ABUSE_CATEGORIES);
export type AbuseCategory = z.infer<typeof AbuseCategorySchema>;
export const ABUSE_CATEGORY_LABELS: Record<AbuseCategory, string> = {
  insults_harassment: 'Insultos o acoso',
  threats: 'Amenazas',
  sexual_content: 'Contenido sexual',
  deliberate_trolling: 'Trolleo o desvío deliberado persistente',
  spam_or_bot: 'Spam o automatizado',
};

export const AI_ABUSE_STATUSES = ['flagged', 'confirmed', 'dismissed', 'lifted'] as const;
export const AiAbuseStatusSchema = z.enum(AI_ABUSE_STATUSES);
export type AiAbuseStatus = z.infer<typeof AiAbuseStatusSchema>;
export const AI_ABUSE_PAUSED_LABEL = 'IA en pausa por posible abuso';
export const AI_BLOCKED_LABEL = 'Bloqueado para IA';
export const AI_ABUSE_STATUS_LABELS: Record<AiAbuseStatus, string> = {
  flagged: AI_ABUSE_PAUSED_LABEL,
  confirmed: AI_BLOCKED_LABEL,
  dismissed: 'Descartado: la IA reanudó',
  lifted: 'Bloqueo levantado',
};

/** Contact-level AI state for the lead view and inbox (multichannel). */
export const AI_ATTENTION_STATES = ['active', 'paused', 'blocked'] as const;
export const AiAttentionStateSchema = z.enum(AI_ATTENTION_STATES);
export type AiAttentionState = z.infer<typeof AiAttentionStateSchema>;

export const AiAbuseCaseSchema = z.object({
  id: Id,
  contactId: Id,
  contactName: z.string().nullable(),
  projectId: Id.nullable(),
  projectName: z.string().nullable(),
  leadInterestId: Id.nullable(),
  channel: z.enum(['whatsapp', 'voice']),
  callId: Id.nullable(),
  conversationId: Id.nullable(),
  category: AbuseCategorySchema,
  evidence: z.string(),
  redirectAttempts: z.number().int(),
  status: AiAbuseStatusSchema,
  reviewTaskId: Id.nullable(),
  flaggedAt: Iso,
  decidedAt: Iso.nullable(),
  decidedByName: z.string().nullable(),
  decisionNote: z.string().nullable(),
  liftedAt: Iso.nullable(),
  liftedByName: z.string().nullable(),
  liftReason: z.string().nullable(),
});
export type AiAbuseCase = z.infer<typeof AiAbuseCaseSchema>;

export const AiAbuseQuerySchema = z.object({
  /** open = flagged (review queue) · blocked = confirmed · all. */
  status: z.enum(['open', 'blocked', 'all']).default('open'),
  contactId: Id.optional(),
});

export const ContactAiAttentionSchema = z.object({
  contactId: Id,
  state: AiAttentionStateSchema,
  /** The case behind a paused/blocked state (null when active). */
  current: AiAbuseCaseSchema.nullable(),
  history: z.array(AiAbuseCaseSchema),
});
export type ContactAiAttention = z.infer<typeof ContactAiAttentionSchema>;

export const DecideAiAbuseSchema = z.object({
  decision: z.enum(['confirm', 'dismiss']),
  note: z.string().trim().min(5).max(500),
});
export type DecideAiAbuse = z.infer<typeof DecideAiAbuseSchema>;

export const LiftAiBlockSchema = z.object({ reason: z.string().trim().min(5).max(500) });
export type LiftAiBlock = z.infer<typeof LiftAiBlockSchema>;
